import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Search, X, Play, Sparkles, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ModulePage";
import StatusBadge from "@/components/StatusBadge";
import ReadAloud from "@/components/ReadAloud";

function Field({ label, children }) {
  return <div><div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div><div className="text-sm">{children}</div></div>;
}

function Detail({ uc, onClose }) {
  const [advisor, setAdvisor] = useState(null);
  const [advLoading, setAdvLoading] = useState(false);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);

  const runAdvisor = async () => { setAdvLoading(true); try { const { data } = await api.post("/advisor", { use_case_id: uc.use_case_id }); setAdvisor(data); } catch {} finally { setAdvLoading(false); } };
  const runTest = async () => { setTesting(true); try { const { data } = await api.post(`/use-cases/${uc.use_case_id}/run-test`); setTest(data); } catch {} finally { setTesting(false); } };

  const raCtx = `${uc.name}. ${uc.business_problem} AI opportunity: ${uc.ai_opportunity}. Risk level ${uc.risk_level}. Human approval: ${uc.human_approval}. Expected outcome: ${uc.expected_outcome}.`;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <div data-testid="uc-detail" className="h-full w-full max-w-2xl overflow-y-auto bg-card p-6 shadow-2xl fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground"><span className="font-mono">{uc.use_case_id}</span><StatusBadge value="DEMO" /></div>
            <h2 className="text-xl font-bold">{uc.name}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">{uc.industry} · {uc.business_function} · {uc.ai_pattern}</div>
          </div>
          <button onClick={onClose} data-testid="uc-detail-close" className="rounded-lg border border-border p-2 hover:bg-secondary"><X className="h-4 w-4" /></button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <StatusBadge value={uc.risk_level} />
          <StatusBadge value={uc.status} />
          <ReadAloud context={raCtx} testId="ra-uc" />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Business Problem">{uc.business_problem}</Field>
          <Field label="AI Opportunity">{uc.ai_opportunity}</Field>
          <Field label="Human Approval">{uc.human_approval}</Field>
          <Field label="Expected Outcome">{uc.expected_outcome}</Field>
          <Field label="Data Classification">{uc.data_classification} · {uc.data_sensitivity}</Field>
          <Field label="Enterprise Systems">{(uc.enterprise_systems || []).join(", ")}</Field>
          <Field label="Estimated Value">${(uc.estimated_value_usd || 0).toLocaleString()} <span className="text-xs text-muted-foreground">(SIMULATED)</span></Field>
          <Field label="Maturity">{uc.maturity_level}</Field>
        </div>

        <h3 className="mb-2 mt-6 text-sm font-semibold">Workflow</h3>
        <div className="rounded-xl border border-border bg-secondary/30 p-3 text-xs">{uc.workflow}</div>

        <h3 className="mb-2 mt-6 text-sm font-semibold">Readiness Scorecard <span className="text-xs font-normal text-muted-foreground">(no misleading overall score)</span></h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {Object.entries(uc.scorecard || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between rounded-xl border border-border bg-secondary/20 px-3 py-2 text-sm">
              <span>{k}</span><StatusBadge value={v} />
            </div>
          ))}
        </div>

        <h3 className="mb-2 mt-6 text-sm font-semibold">10 Advisory Dimensions</h3>
        <div className="space-y-1.5">
          {Object.entries(uc.dimension_scores || {}).map(([k, v]) => (
            <div key={k}>
              <div className="mb-0.5 flex justify-between text-xs"><span>{k}</span><span className="font-mono">{v}/100</span></div>
              <div className="h-1.5 w-full rounded-full bg-secondary"><div className="h-full rounded-full bg-accent" style={{ width: `${v}%` }} /></div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button data-testid="uc-run-test" onClick={runTest} disabled={testing} className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run Test Suite</button>
          <button data-testid="uc-advisor" onClick={runAdvisor} disabled={advLoading} className="flex items-center gap-2 rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground disabled:opacity-50">{advLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI Advisor</button>
        </div>

        {test && (
          <div className="mt-4 rounded-xl border border-border p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">Test Results <StatusBadge value="SIMULATED" /></div>
            {test.results.map((r) => <div key={r.phase} className="flex items-center justify-between border-b border-border/50 py-1.5 text-sm"><span>{r.phase} <span className="text-xs text-muted-foreground">({r.latency_ms}ms)</span></span><StatusBadge value={r.status} /></div>)}
          </div>
        )}
        {advisor && (
          <div className="mt-4 rounded-xl border border-border p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold">Advisory Assessment</span><div className="flex gap-2"><StatusBadge value={advisor.source} /><ReadAloud context={advisor.analysis} testId="ra-uc-adv" /></div></div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{advisor.analysis}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function UseCases() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", industry: "", function: "", pattern: "", risk: "", category: "" });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const limit = 24;

  useEffect(() => { api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { ...filters, page, limit };
    Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
    api.get("/use-cases", { params }).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  const pages = Math.ceil(data.total / limit);
  const set = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  return (
    <div>
      <PageHeader title="1,000 Use-Case Test Center" subtitle="An executable advisory library — search, filter, assess readiness and run governed AI test suites." readAloudContext={`Use case library with ${data.total} cases. Filter by industry, function, AI pattern and risk. Each case is an executable test scenario with a readiness scorecard.`} />
      <Card className="mb-4">
        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute inset-y-0 my-auto h-4 w-4 text-muted-foreground" style={{ insetInlineStart: "0.75rem" }} />
            <input data-testid="uc-search" value={filters.search} onChange={(e) => set("search", e.target.value)} placeholder="Search 1,000+ use cases…" className="w-full rounded-xl border border-border bg-background py-2.5 ps-9 pe-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>
        {meta && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {[["industry", meta.industries], ["function", meta.functions], ["pattern", meta.patterns], ["risk", meta.risk_levels], ["category", meta.categories]].map(([key, opts]) => (
              <select key={key} data-testid={`uc-filter-${key}`} value={filters[key]} onChange={(e) => set(key, e.target.value)} className="rounded-xl border border-border bg-background px-2 py-2 text-xs">
                <option value="">All {key}</option>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            <button data-testid="uc-clear" onClick={() => { setFilters({ search: "", industry: "", function: "", pattern: "", risk: "", category: "" }); setPage(1); }} className="rounded-xl border border-border px-2 py-2 text-xs hover:bg-secondary">Clear</button>
          </div>
        )}
      </Card>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((uc) => (
            <button key={uc.use_case_id} data-testid={`uc-card-${uc.use_case_id}`} onClick={() => setSelected(uc)} className="group rounded-2xl border border-border bg-card p-4 text-start shadow-sm transition-colors hover:border-accent/60">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] text-muted-foreground">{uc.use_case_id}</span>
                <StatusBadge value={uc.risk_level} />
              </div>
              <h3 className="mb-1 line-clamp-2 text-sm font-semibold">{uc.name}</h3>
              <p className="mb-3 line-clamp-2 text-xs text-muted-foreground">{uc.business_problem}</p>
              <div className="flex flex-wrap gap-1.5 text-[10px] text-muted-foreground">
                <span className="rounded-md bg-secondary px-1.5 py-0.5">{uc.industry}</span>
                <span className="rounded-md bg-secondary px-1.5 py-0.5">{uc.ai_pattern}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <StatusBadge value={uc.status} />
                <span className="flex items-center gap-1 text-accent group-hover:gap-2 transition-all">Open <ChevronRight className="h-3.5 w-3.5" /></span>
              </div>
            </button>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button data-testid="uc-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-xl border border-border px-3 py-1.5 text-sm disabled:opacity-40">Prev</button>
          <span className="text-sm text-muted-foreground">Page {page} / {pages}</span>
          <button data-testid="uc-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-xl border border-border px-3 py-1.5 text-sm disabled:opacity-40">Next</button>
        </div>
      )}

      {selected && <Detail uc={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
